/** 2열 그리드 — 번호·저장·로드 모두 세로(열) 우선 */

export const TWO_COL_ITEMS_PER_ROW = 2;
export const TWO_COL_SLOT_COLS = 3;

export function sequentialIdxFromVisual(visualRow: number, slot: number, visualRowCount: number): number {
  if (slot === 0) return visualRow;
  return visualRowCount + visualRow;
}

export function displayItemNumber(visualRow: number, slot: number, visualRowCount: number): number {
  return sequentialIdxFromVisual(visualRow, slot, visualRowCount) + 1;
}

export function slotFromCol(col: number): number {
  return Math.floor(col / TWO_COL_SLOT_COLS);
}

export function isNumberCol(col: number): boolean {
  return col % TWO_COL_SLOT_COLS === 0;
}

export function valueColInSlot(slot: number): number {
  return slot * TWO_COL_SLOT_COLS + 1;
}

export function memoColInSlot(slot: number): number {
  return slot * TWO_COL_SLOT_COLS + 2;
}

function padToEvenLength<T>(items: T[], minCount: number, emptyItem: () => T): T[] {
  const padded = [...items];
  while (padded.length < minCount) {
    padded.push(emptyItem());
  }
  if (padded.length % TWO_COL_ITEMS_PER_ROW !== 0) {
    padded.push(emptyItem());
  }
  return padded;
}

export function itemsToTwoColumnGrid<T>(
  items: T[],
  minCount: number,
  emptyItem: () => T,
  toCells: (item: T) => [string, string],
): string[][] {
  const padded = padToEvenLength(items, minCount, emptyItem);
  const visualRowCount = padded.length / TWO_COL_ITEMS_PER_ROW;
  const visualRows: string[][] = [];
  for (let r = 0; r < visualRowCount; r++) {
    const row: string[] = [];
    for (let slot = 0; slot < TWO_COL_ITEMS_PER_ROW; slot++) {
      const seqIdx = sequentialIdxFromVisual(r, slot, visualRowCount);
      const item = padded[seqIdx] ?? emptyItem();
      const [value, memo] = toCells(item);
      row.push('', value, memo);
    }
    visualRows.push(row);
  }
  return visualRows;
}

export function gridToSequentialItems<T>(
  data: string[][],
  ids: (string | null)[],
  fromCells: (id: string | null, value: string, memo: string) => T,
): T[] {
  const visualRowCount = data.length;
  const items: T[] = [];
  for (let slot = 0; slot < TWO_COL_ITEMS_PER_ROW; slot++) {
    for (let r = 0; r < visualRowCount; r++) {
      const seqIdx = sequentialIdxFromVisual(r, slot, visualRowCount);
      const id = ids[seqIdx] ?? null;
      const value = String(data[r][valueColInSlot(slot)] || '').trim();
      const memo = String(data[r][memoColInSlot(slot)] || '').trim();
      items.push(fromCells(id, value, memo));
    }
  }
  return items;
}

export function expandItemsForNewVisualRow<T>(items: T[], oldVisualRowCount: number, emptyItem: () => T): T[] {
  const newVisualRowCount = oldVisualRowCount + 1;
  const next: T[] = [];
  for (let r = 0; r < newVisualRowCount; r++) {
    if (r < oldVisualRowCount) {
      next.push(items[sequentialIdxFromVisual(r, 0, oldVisualRowCount)] ?? emptyItem());
    } else {
      next.push(emptyItem());
    }
  }
  for (let r = 0; r < newVisualRowCount; r++) {
    if (r < oldVisualRowCount) {
      next.push(items[sequentialIdxFromVisual(r, 1, oldVisualRowCount)] ?? emptyItem());
    } else {
      next.push(emptyItem());
    }
  }
  return next;
}

export function remapIdsColumnMajor(
  oldData: string[][],
  oldIds: (string | null)[],
  newData: string[][],
): (string | null)[] {
  const oldVisualRowCount = oldData.length;
  const newVisualRowCount = newData.length;
  const total = newVisualRowCount * TWO_COL_ITEMS_PER_ROW;
  const newIds: (string | null)[] = Array(total).fill(null);

  for (let slot = 0; slot < TWO_COL_ITEMS_PER_ROW; slot++) {
    for (let r = 0; r < newVisualRowCount; r++) {
      const seqIdx = sequentialIdxFromVisual(r, slot, newVisualRowCount);
      const value = String(newData[r][valueColInSlot(slot)] || '').trim();
      const memo = String(newData[r][memoColInSlot(slot)] || '').trim();
      if (r < oldVisualRowCount) {
        const oldSeqIdx = sequentialIdxFromVisual(r, slot, oldVisualRowCount);
        const oldValue = String(oldData[r][valueColInSlot(slot)] || '').trim();
        const oldMemo = String(oldData[r][memoColInSlot(slot)] || '').trim();
        if (value === oldValue && memo === oldMemo) {
          newIds[seqIdx] = oldIds[oldSeqIdx] ?? null;
        }
      }
    }
  }
  return newIds;
}
